"use client";

import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay2.png";
import { useRouter, useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { AnimatePresence, motion } from "motion/react";
import { Check, Cloud, Loader2 } from "lucide-react";
import { User } from "iconoir-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  NORTHSTAR_ID_PARAM,
  NORTHSTAR_QUERY_PARAM,
  NORTHSTAR_QUERY_VALUE,
  RESUME_QUERY_PARAM,
  RESUME_QUERY_VALUE,
  stepVariants,
  stepTransition,
  WIZARD_STEPS,
} from "../_logic/constants";
import { loadNorthstarId, loadWizardState, saveNorthstarId } from "../_logic/wizard-storage";
import { getNorthstarRequest } from "../_logic/northstar-actions";
import { resolveCityToLocation } from "@/lib/events/location-resolution";
import { hydrateFormDataFromDraft } from "../_logic/draft-actions";
import { blankOut, buildFreshStartPrefill } from "../_logic/fresh-start-prefill";
import {
  isAboutYourEventDone,
  isAgendaDone,
  isBudgetDecisionDone,
  isDatesDone,
  isFoodBeverageDone,
  isGuestroomsDone,
  isHeadcountDone,
  isMeetingRoomsDone,
} from "../_logic/step-completion";
import type { FormattedLocation } from "@/hooks/useLocationSearch";
import type { DraftEvents } from "@/lib/database/events";
import type { AdminLaunchContext } from "../_logic/admin-org-context";
import type { OfficeSuggestion } from "../_types/wizard-types";
import { OfficeSuggestionsProvider } from "../_context/OfficeSuggestionsContext";
import { INITIAL_FORM_DATA, useWizardState } from "../_hooks/use-wizard-state";
import { useWizardNavigation } from "../_hooks/use-wizard-navigation";
import { useDraftAutosave } from "../_hooks/use-draft-autosave";
import { useHasMounted } from "@/hooks/useHasMounted";
import { useUser } from "@/hooks/useUser";
import Logo from "@/components/ui/Logo";
import { WelcomeNorthstarModal } from "./WelcomeNorthstarModal";
import { BackToDashboardLink } from "./BackToDashboardLink";
import { WizardProgressBar } from "./WizardProgressBar";
import { AboutYourEventStep } from "./steps/AboutYourEventStep";
import { GuestroomsStep } from "./steps/GuestroomsStep";
import { ResumeLostBanner } from "./ResumeLostBanner";
import { ResumeDraftBanner } from "./ResumeDraftBanner";
import { PrefilledAnswersBanner } from "./PrefilledAnswersBanner";
import { AdminContextBanner } from "./AdminContextBanner";
import { AdminOrgBanner } from "./AdminOrgBanner";

/** Shown while a code-split step/results chunk loads — matches the wizard's existing spinner style. */
const StepLoadingFallback = () => (
  <div className="flex items-center justify-center h-[420px]">
    <Loader2 className="size-6 text-indigo-400 animate-spin" />
  </div>
);

// Code-split every step but the first (About-your-event stays static) — the rest pull in antd date
// pickers, @dnd-kit, and the legacy agenda component tree that step 1 never needs, and several
// (Agenda, Final Details) are skippable. Guestrooms also stays static since it's a light, common
// step; Dates is the one unavoidable loading gap (right after step 1) given its antd/@dnd-kit weight.
const PlanningDocStep = dynamic(() => import("./steps/PlanningDocStep").then((m) => m.PlanningDocStep), {
  loading: StepLoadingFallback,
});
const MeetingRoomsStep = dynamic(() => import("./steps/MeetingRoomsStep").then((m) => m.MeetingRoomsStep), {
  loading: StepLoadingFallback,
});
const FoodBeverageStep = dynamic(() => import("./steps/FoodBeverageStep").then((m) => m.FoodBeverageStep), {
  loading: StepLoadingFallback,
});
const DatesStep = dynamic(() => import("./steps/DatesStep").then((m) => m.DatesStep), {
  loading: StepLoadingFallback,
});
const HeadcountStep = dynamic(() => import("./steps/HeadcountStep").then((m) => m.HeadcountStep), {
  loading: StepLoadingFallback,
});
const AgendaStep = dynamic(() => import("./steps/agenda/AgendaStep").then((m) => m.AgendaStep), {
  loading: StepLoadingFallback,
});
const BudgetDecisionStep = dynamic(() => import("./steps/BudgetDecisionStep").then((m) => m.BudgetDecisionStep), {
  loading: StepLoadingFallback,
});
const FinalDetailsStep = dynamic(() => import("./steps/FinalDetailsStep").then((m) => m.FinalDetailsStep), {
  loading: StepLoadingFallback,
});
const VenuePreviewResults = dynamic(() => import("./results/VenuePreviewResults").then((m) => m.VenuePreviewResults), {
  loading: StepLoadingFallback,
});
import type { CreateEventFormData, WizardStep } from "../_types/wizard-types";

interface ResumeState {
  initialFormData?: CreateEventFormData;
  initialCurrentStep?: WizardStep;
  initialCompletedSteps?: Set<WizardStep>;
  initialIsComplete?: boolean;
  showLostBanner: boolean;
  /** Set when an authenticated session silently resumed an existing draft, so the wizard can tell
   *  the user rather than dropping them mid-fill with no explanation. Never set for a fresh start,
   *  a Northstar session, or an anonymous visitor. */
  resumedDraftName?: string | null;
  /** The answers a fresh start was filled in with from the user's last event, if any. */
  prefill?: Partial<CreateEventFormData>;
}

/**
 * Computes the guest session's initial state from what's currently in sessionStorage — ported
 * verbatim from v2's guest-resume mechanism. Applies to every anonymous wizard fill that hits the
 * auth gate at final submit, Northstar lead or plain self-serve visitor alike (both are anonymous
 * during the fill and only authenticate at the end). Only ever called when `?resume=1` is present —
 * the caller gates on that itself, so this doesn't re-check it.
 *
 * - Stored state found → hydrate at the final step, skip to results.
 * - Nothing found → signal "lost" (`showLostBanner: true`) so the caller can fall back to an
 *   authenticated draft before giving up and showing the "form was lost" banner as a last resort.
 */
function computeGuestResumeContext(): ResumeState {
  const saved = loadWizardState();
  if (!saved) return { showLostBanner: true };

  const allSteps = new Set<WizardStep>(WIZARD_STEPS.map((s) => s.id));
  const lastStep = WIZARD_STEPS[WIZARD_STEPS.length - 1].id;

  return {
    initialFormData: saved,
    initialCurrentStep: lastStep,
    initialCompletedSteps: allSteps,
    initialIsComplete: true,
    showLostBanner: false,
  };
}

/**
 * Detects a Northstar partner redirect on mount and returns its opaque id, or `null`. Prefers the
 * `northstar=1` + `ns_id` URL params (a fresh redirect), then falls back to the id persisted in
 * sessionStorage — so the welcome popup re-shows on a plain refresh even though we strip the id from
 * the URL after arrival. Returns `null` during a post-login resume (the lead has already seen it;
 * resume jumps straight to results).
 */
function computeNorthstarNsId(searchParams: URLSearchParams): string | null {
  if (searchParams.get(RESUME_QUERY_PARAM) === RESUME_QUERY_VALUE) return null;
  const urlNsId =
    searchParams.get(NORTHSTAR_QUERY_PARAM) === NORTHSTAR_QUERY_VALUE ? searchParams.get(NORTHSTAR_ID_PARAM) : null;
  return urlNsId ?? loadNorthstarId();
}

/**
 * Finds the first step with missing/invalid required data in the given (hydrated) form data, for
 * landing a resumed authenticated draft at the right place. Recomputed fresh from `formData` rather
 * than cached, so going back and changing an earlier answer re-surfaces later steps correctly.
 *
 * @param formData - Hydrated wizard form data.
 * @returns The step to land on, the set of steps before it (all complete), and whether every step is
 *   done.
 */
function computeFurthestStep(formData: CreateEventFormData): {
  step: WizardStep;
  completed: Set<WizardStep>;
  complete: boolean;
} {
  const completed = new Set<WizardStep>();

  if (!isAboutYourEventDone(formData)) return { step: "about-your-event", completed, complete: false };
  completed.add("about-your-event");
  // Planning doc is optional and leaves no footprint of its own, so it counts as done as soon as the
  // step before it is: a resumed draft with a name and location has already been offered the upload.
  completed.add("planning-doc");

  if (!isDatesDone(formData)) return { step: "dates", completed, complete: false };
  completed.add("dates");

  if (!isHeadcountDone(formData)) return { step: "headcount", completed, complete: false };
  completed.add("headcount");

  if (!isGuestroomsDone(formData)) return { step: "guestrooms", completed, complete: false };
  completed.add("guestrooms");

  if (!isMeetingRoomsDone(formData)) return { step: "meeting-rooms", completed, complete: false };
  completed.add("meeting-rooms");

  if (!isFoodBeverageDone(formData)) return { step: "food-beverage", completed, complete: false };
  completed.add("food-beverage");

  // Agenda is entirely optional (skippable with zero footprint) — same class of problem as Final
  // Details below. There's no data-driven way to distinguish "skipped" from "never reached", so any
  // engagement signal (a session, or either requirements field) is treated as done; otherwise resume
  // conservatively lands back on Agenda even if the user had actually progressed further.
  if (!isAgendaDone(formData)) return { step: "agenda", completed, complete: false };
  completed.add("agenda");

  if (!isBudgetDecisionDone(formData)) return { step: "budget-decision", completed, complete: false };
  completed.add("budget-decision");

  // Final Details is entirely optional — there's no data-driven way to tell "already finished" from
  // "never started" for an all-optional step. Land the user there rather than guessing `complete`;
  // its own terminal Continue is what actually sets `isComplete` on real completion.
  return { step: "final-details", completed, complete: false };
}

/**
 * Resolves the authenticated (non-Northstar) resume state from a server-fetched draft — synchronous,
 * since the draft was already loaded server-side in `src/app/create/page.tsx` and handed down as a
 * prop, rather than fetched client-side after mount.
 *
 * @param draft - The user's most recent open draft, or `null` if they have none.
 */
function computeAuthenticatedResumeContext(draft: DraftEvents | null): ResumeState {
  if (!draft) return { showLostBanner: false };
  const merged: CreateEventFormData = { ...INITIAL_FORM_DATA, ...hydrateFormDataFromDraft(draft) };
  const { step, completed, complete } = computeFurthestStep(merged);
  return {
    initialFormData: merged,
    initialCurrentStep: step,
    initialCompletedSteps: completed,
    initialIsComplete: complete,
    showLostBanner: false,
    resumedDraftName: draft.name ?? null,
  };
}

/**
 * Resolves a fresh start: blank, or filled in from the user's last event when there is one.
 *
 * @param previousEventDraft - The user's most recent submitted draft, or `null`.
 * @returns The resume state for a fresh start.
 */
function computeFreshStartContext(previousEventDraft: DraftEvents | null): ResumeState {
  const prefill = buildFreshStartPrefill(previousEventDraft);
  if (!prefill) return { showLostBanner: false };
  return { initialFormData: { ...INITIAL_FORM_DATA, ...prefill }, showLostBanner: false, prefill };
}

interface CreateEventWizardProps {
  /** The signed-in user's most recent open draft, fetched server-side in `page.tsx`. `null` for a
   *  Northstar session (never used — Northstar resume reads sessionStorage instead), an anonymous
   *  self-serve session (never fetched — no signed-in user to look up a draft for), or a brand-new
   *  authenticated session with no draft yet. */
  initialDraft: DraftEvents | null;
  /** The signed-in user's last submitted draft, used to fill in a fresh start. `null` whenever
   *  `initialDraft` is set, and for anonymous, Northstar and admin-launched sessions. */
  previousEventDraft: DraftEvents | null;
  /** The org/owner context for an admin-launched creation (ENG-918), resolved + authorized
   *  server-side in `page.tsx`. Both fields `null` for a normal customer visit. */
  adminLaunchContext: AdminLaunchContext;
  /** The enterprise's stored office locations, offered on the location step. Empty for an anonymous
   *  visitor or an enterprise with no offices. */
  officeSuggestions: OfficeSuggestion[];
  /** Whether the request carried a signed-in session, resolved server-side. Decides the final
   *  action without waiting on the client user store, which starts out empty. */
  isSignedIn: boolean;
}

/** Full-page spinner held on a guest resume until the wizard can mount in the browser. Matches
 *  `WizardLoadingFallback` in `src/app/create/page.tsx`, so the hand-off from the page's Suspense
 *  fallback doesn't visibly change. */
const ResumeMountFallback = () => (
  <div className="flex min-h-screen items-center justify-center" aria-busy="true">
    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" aria-label="Loading" />
  </div>
);

/**
 * Root client component for the create event v3 wizard.
 *
 * A guest resume (`?resume=1`) restores the fill from sessionStorage, which the server can't read: it
 * would render the "we couldn't restore your answers" state while the browser renders the restored
 * one, a hydration mismatch (React #418) on every guest returning from login. So that path renders a
 * spinner until mount, and the wizard's resume state is only ever computed in the browser. Every
 * other path resolves from server data (`initialDraft`) and keeps rendering on the server.
 *
 * @param props - See {@link CreateEventWizardProps}.
 * @returns The wizard, or a spinner while a guest resume waits for the client.
 */
export const CreateEventWizard = (props: CreateEventWizardProps): React.ReactElement => {
  // Hooks
  const searchParams = useSearchParams();
  const hasMounted = useHasMounted();

  // Derived values
  // Read live rather than frozen: the wizard strips `?resume=1` after mount, but by then
  // `hasMounted` is true and stays true, so the strip can't unmount the wizard.
  const isResumeRequest = searchParams?.get(RESUME_QUERY_PARAM) === RESUME_QUERY_VALUE;

  // Render
  if (isResumeRequest && !hasMounted) return <ResumeMountFallback />;
  return <CreateEventWizardResumeResolver {...props} />;
};

/** Resolves the wizard's starting state (Northstar id, guest resume, or authenticated draft) once,
 *  at mount, and renders the wizard body with it. */
const CreateEventWizardResumeResolver = ({
  initialDraft,
  previousEventDraft,
  adminLaunchContext,
  officeSuggestions,
  isSignedIn,
}: CreateEventWizardProps) => {
  // Hooks
  const searchParams = useSearchParams();

  // States — Northstar detection is one-shot; subsequent renders see the stripped URL. Both resume
  // paths resolve synchronously (Northstar from sessionStorage, authenticated from the
  // server-fetched `initialDraft`), so there's no async gate before the wizard's own hooks (which
  // read their initial step/completed-steps on first render) can mount.
  const [northstarNsId] = useState(() => computeNorthstarNsId(new URLSearchParams(searchParams?.toString() ?? "")));
  // The guest → auth → resume roundtrip is signaled by `?resume=1` alone — it covers every anonymous
  // wizard fill (Northstar lead or plain self-serve visitor), not just Northstar sessions. Gating this
  // on Northstar-session detection was wrong: a non-Northstar guest has no ns_id by definition, so
  // `?resume=1` always fell through to `computeAuthenticatedResumeContext`, which has no sessionStorage
  // data for an anonymous fill and silently reset the wizard to step one with no explanation.
  // Frozen at mount (not a plain const) for the same reason as `northstarNsId` above: a later effect
  // strips `?resume=1` from the URL via `router.replace`, which would otherwise flip this back to
  // false on the next render — before the venue-preview fetch resolves and `isGuestResume` gets read
  // to decide whether to auto-submit the event, silently breaking that auto-submit every time.
  const [isResumeRequest] = useState(() => searchParams?.get(RESUME_QUERY_PARAM) === RESUME_QUERY_VALUE);
  const [resumeState] = useState<ResumeState>(() => {
    if (!isResumeRequest) {
      return initialDraft
        ? computeAuthenticatedResumeContext(initialDraft)
        : computeFreshStartContext(previousEventDraft);
    }

    const guestContext = computeGuestResumeContext();
    if (!guestContext.showLostBanner) return guestContext;

    // sessionStorage was empty (e.g. the OTP step completed in a different browser/tab) — fall back
    // to the signed-in user's real server-side draft before giving up, so an authenticated user with
    // actual saved progress doesn't see "let's start fresh" when their answers are sitting right
    // there in `initialDraft`.
    const authenticatedContext = computeAuthenticatedResumeContext(initialDraft);
    return authenticatedContext.initialFormData ? authenticatedContext : guestContext;
  });

  return (
    <OfficeSuggestionsProvider offices={officeSuggestions}>
      <CreateEventWizardInner
        northstarNsId={northstarNsId}
        resumeState={resumeState}
        isGuestResume={isResumeRequest}
        adminLaunchContext={adminLaunchContext}
        isSignedIn={isSignedIn}
      />
    </OfficeSuggestionsProvider>
  );
};

interface CreateEventWizardInnerProps {
  northstarNsId: string | null;
  resumeState: ResumeState;
  /** Whether this mount is a guest returning from the auth redirect (`?resume=1`) — used only to
   *  decide whether to move focus into the content region on mount, since that's the moment a
   *  screen-reader user needs to be told something happened (either "we lost your data" or "you've
   *  been jumped straight to results"), not a normal fresh page load. */
  isGuestResume: boolean;
  /** The org/owner context for an admin-launched creation (ENG-918). Shown as a persistent banner
   *  and threaded to the final submit call. Both fields `null` for a normal customer visit. */
  adminLaunchContext: AdminLaunchContext;
  /** See {@link CreateEventWizardProps.isSignedIn}. */
  isSignedIn: boolean;
}

/** Wizard body — only mounted once `resumeState` is resolved, so `useWizardState`/`useWizardNavigation`
 *  see the correct initial values on their first render. */
const CreateEventWizardInner = ({
  northstarNsId,
  resumeState,
  isGuestResume,
  adminLaunchContext,
  isSignedIn,
}: CreateEventWizardInnerProps) => {
  // Hooks
  const router = useRouter();
  const searchParams = useSearchParams();

  // States
  const [formData, dispatch] = useWizardState(resumeState.initialFormData);
  const { currentStep, currentIndex, direction, completedSteps, isComplete, goForward, goBack, goToStep } =
    useWizardNavigation({
      initialCurrentStep: resumeState.initialCurrentStep,
      initialCompletedSteps: resumeState.initialCompletedSteps,
      initialIsComplete: resumeState.initialIsComplete,
    });
  const [showNorthstarWelcome, setShowNorthstarWelcome] = useState(!!northstarNsId);
  const hasAnimated = useRef(false);
  // Focus target for a guest returning from the auth redirect — see the mount effect below.
  const resumeAnnounceRef = useRef<HTMLDivElement>(null);
  // FetchButton resolves its themed color from the --indigo-* CSS vars via getComputedStyle, which is
  // undefined during SSR — so a server-rendered primary button bakes in the non-branded fallback color
  // and (after a hydration mismatch) never repaints. Gating the step/results content on `mounted` keeps
  // those buttons out of the server HTML so they mount fresh on the client and read the enterprise vars,
  // picking up the domain's brand color. Scoped to the wizard (ENG-146); FetchButton is a shared
  // primitive we deliberately aren't touching here. Mirrors the isMounted gate used in dashboard/explore.
  const [mounted, setMounted] = useState(false);
  const [showPrefillBanner, setShowPrefillBanner] = useState(!!resumeState.prefill);

  const {
    isSaving: isDraftSaving,
    hasUnsavedChanges: hasUnsavedDraftChanges,
    lastSaved: lastDraftSaved,
  } = useDraftAutosave(formData, dispatch);
  const { user } = useUser();
  // Only signed-in, non-Northstar sessions actually autosave (see useDraftAutosave), so the status
  // line is meaningless — and would flash for no reason — for anyone else.
  const showAutosaveStatus = !!user && !formData.northstarRequestId;

  // Effects
  useEffect(() => {
    setMounted(true);
  }, []);
  // Flip hasAnimated only after the first mounted render so the initial step still appears in place
  // ("center") rather than sliding in; subsequent step changes animate with "enter".
  useEffect(() => {
    if (mounted) hasAnimated.current = true;
  }, [mounted]);
  // A guest returning from the auth redirect lands on either the "we lost your data" banner or a
  // jump straight to results — a much bigger context change than a normal step transition, and one
  // a screen-reader user has no way to discover without this: move focus into the content region as
  // soon as it renders (gated on `mounted`, same as the content itself) so assistive tech picks up
  // reading from there instead of silently sitting wherever focus already was (usually the body).
  useEffect(() => {
    if (mounted && isGuestResume) resumeAnnounceRef.current?.focus();
    // Intentionally omitting isGuestResume/mounted from deps beyond what's needed to fire once
    // content first mounts — this is a one-time "just arrived" announcement, not a per-render effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // Hydrate the wizard with the Northstar request's venues + id (the popup is already open with
  // static copy, so this runs in the background). The id is known synchronously from the URL, so we
  // persist it into wizard state immediately — that way it survives the auth round-trip (via
  // sessionStorage) even if the venue fetch is slow or fails. Then strip it from the URL so it
  // doesn't leak via Referer / sharing.
  useEffect(() => {
    const nsId = northstarNsId;
    if (!nsId) return;
    // Persist the id for this session so a refresh re-shows the popup, then strip it from the URL.
    saveNorthstarId(nsId);
    dispatch({ type: "SET_NORTHSTAR_CONTEXT", payload: { requestId: nsId, venueIds: [] } });
    router.replace("/create");
    let cancelled = false;
    getNorthstarRequest(nsId)
      .then(async ({ venueIds, cities }) => {
        if (cancelled) return;
        if (venueIds.length > 0) {
          dispatch({ type: "SET_NORTHSTAR_CONTEXT", payload: { requestId: nsId, venueIds } });
        }
        if (cities.length === 0) return;
        // Resolve the requested venues' cities into real FormattedLocations and prefill the location
        // step. PREFILL_LOCATIONS is non-destructive (only fills when the user has no location yet), so
        // this can land late without clobbering a manual choice.
        const resolved = (await Promise.all(cities.map(resolveCityToLocation))).filter(
          (loc): loc is FormattedLocation => loc !== null
        );
        if (cancelled || resolved.length === 0) return;
        dispatch({ type: "PREFILL_LOCATIONS", payload: { formattedLocations: resolved } });
      })
      .catch((err) => console.error("CreateEventWizard: failed to load Northstar request", err));
    return () => {
      cancelled = true;
    };
    // Intentionally empty deps: runs once with the initial searchParams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Strip ?resume=1 from the URL once on mount so a refresh starts clean.
  useEffect(() => {
    if (searchParams?.get(RESUME_QUERY_PARAM) === RESUME_QUERY_VALUE) {
      router.replace("/create");
    }
    // Intentionally empty deps: runs once with the initial searchParams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived values
  // The first step has no back target, so omit onBack at index 0 — derived from the step order
  // rather than hardcoded per case, so reordering WIZARD_STEPS stays correct automatically.
  // Memoized so its identity is stable across re-renders that don't actually change these values
  // (e.g. `mounted`/`showNorthstarWelcome` flipping elsewhere in this component).
  const sharedProps = useMemo(
    () => ({
      formData,
      dispatch,
      onNext: goForward,
      onBack: currentIndex === 0 ? undefined : goBack,
      entryDirection: direction,
    }),
    [formData, dispatch, goForward, goBack, currentIndex, direction]
  );

  // Handlers
  const handleStartBlank = () => {
    if (resumeState.prefill) dispatch({ type: "HYDRATE_DRAFT", payload: blankOut(resumeState.prefill) });
    setShowPrefillBanner(false);
  };

  // Render
  const renderCurrentStep = () => {
    switch (currentStep) {
      case "about-your-event":
        return <AboutYourEventStep {...sharedProps} />;
      case "planning-doc":
        return <PlanningDocStep {...sharedProps} isSignedIn={isSignedIn} lastEventPrefill={resumeState.prefill} />;
      case "dates":
        return <DatesStep {...sharedProps} />;
      case "headcount":
        return <HeadcountStep {...sharedProps} />;
      case "guestrooms":
        return <GuestroomsStep {...sharedProps} />;
      case "meeting-rooms":
        return <MeetingRoomsStep {...sharedProps} />;
      case "food-beverage":
        return <FoodBeverageStep {...sharedProps} />;
      case "agenda":
        return <AgendaStep {...sharedProps} />;
      case "budget-decision":
        return <BudgetDecisionStep {...sharedProps} />;
      case "final-details":
        return <FinalDetailsStep {...sharedProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen w-screen bg-landing-primary relative overflow-y-auto">
      <WelcomeNorthstarModal isOpen={showNorthstarWelcome} onClose={() => setShowNorthstarWelcome(false)} />

      {/* Background overlay */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `url(${backgroundOverlay.src})`,
          backgroundSize: "100% auto",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          opacity: 0.2,
        }}
      />

      {/* Logo — enterprise-branded via domain (falls back to Nowadays when no enterprise logo) */}
      <div className="absolute top-4 left-4 md:top-6 md:left-8 z-10 flex flex-col items-start gap-2">
        <Logo size="md" priority />
        {/* Renders only for a signed-in user, so the anonymous fill keeps its
            chrome-free page. Sits under the logo rather than in the opposite
            corner, which the autosave indicator already owns. */}
        <BackToDashboardLink />
      </div>

      {/* Out of flow (mirrors the Logo's corner positioning) so it never reflows the progress bar,
          resume banner, or step content as it appears/disappears while typing. Icon/copy/color
          convention matches the other builders' autosave indicators (e.g. landing-page-v2's
          `SaveStatusIndicator`, the email builder) rather than inventing a new one. */}
      {showAutosaveStatus && (isDraftSaving || hasUnsavedDraftChanges || lastDraftSaved) && (
        <div
          role="status"
          className={`absolute top-4 right-4 md:top-6 md:right-8 z-10 flex items-center gap-1.5 text-xs font-medium ${
            isDraftSaving ? "text-gray-500" : hasUnsavedDraftChanges ? "text-amber-600" : "text-green-600"
          }`}
        >
          {isDraftSaving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : hasUnsavedDraftChanges ? (
            <Cloud className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          <span>{isDraftSaving ? "Saving…" : hasUnsavedDraftChanges ? "Unsaved changes" : "Saved"}</span>
        </div>
      )}

      {/* Centered content — a fixed width across every step, including Agenda's calendar preview, so
          the question/button column never resizes as the user moves through the wizard. `m-auto`
          (not `justify-center`) centers the column: when a step's content is taller than the
          viewport, `justify-center` on a fixed-height flex parent clips the start of the overflow
          (the user would have to scroll up first to see it) — `m-auto` on the child instead just
          stops centering and aligns to the top once it overflows, which is what a scrollable page
          needs. */}
      <div
        ref={resumeAnnounceRef}
        tabIndex={-1}
        className="relative z-1 min-h-screen flex flex-col items-center px-6 py-10 outline-none"
      >
        {/* Pinned to the top, outside the centered block below — otherwise a short step's content
            (e.g. a bare Yes/No gate) centers the whole column vertically and drags the progress bar
            down with it, so it visibly jumps position as the user moves between short and tall steps. */}
        <div className="w-full max-w-[560px] flex flex-col gap-12 mb-12">
          <WizardProgressBar currentStep={currentStep} completedSteps={completedSteps} onStepClick={goToStep} />

          {/* Below the progress bar (not above it): one combined notice when both org and owner are
              known, instead of two stacked boxes repeating the "Admin:" prefix. A VA admin can clear
              the org gate but not the mandatory owner one, so owner-context alone never happens here
              (see resolveAdminCreateEntryDecision) -- only "org alone" or "both" are real states. */}
          {adminLaunchContext.owner ? (
            <AdminContextBanner icon={<User className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}>
              Admin: creating for <span className="font-semibold">{adminLaunchContext.owner.ownerName}</span>
              {adminLaunchContext.org && (
                <>
                  {" "}
                  at <span className="font-semibold">{adminLaunchContext.org.orgName}</span>
                </>
              )}
            </AdminContextBanner>
          ) : (
            adminLaunchContext.org && <AdminOrgBanner orgName={adminLaunchContext.org.orgName} />
          )}

          {resumeState.showLostBanner && <ResumeLostBanner />}
          {!resumeState.showLostBanner && resumeState.resumedDraftName !== undefined && (
            <ResumeDraftBanner draftName={resumeState.resumedDraftName} />
          )}
          {showPrefillBanner && (
            <PrefilledAnswersBanner onStartBlank={handleStartBlank} onDismiss={() => setShowPrefillBanner(false)} />
          )}
        </div>

        <div className="w-full max-w-[560px] flex flex-col gap-12 m-auto">
          {/* Gated on `mounted` so its FetchButtons mount client-side and pick up the enterprise brand
              color (see the note by the `mounted` state). Steps animate in on mount, matching the
              existing entrance, so there's no visible flash beyond the logo/progress bar already shown. */}
          {mounted &&
            (isComplete ? (
              <VenuePreviewResults
                formData={formData}
                skipProgress={!!resumeState.initialIsComplete}
                isGuestResume={isGuestResume}
                adminLaunchContext={adminLaunchContext}
                isSignedIn={isSignedIn}
              />
            ) : (
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentStep}
                  custom={direction}
                  variants={stepVariants}
                  initial={hasAnimated.current ? "enter" : "center"}
                  animate="center"
                  exit="exit"
                  transition={stepTransition}
                >
                  {renderCurrentStep()}
                </motion.div>
              </AnimatePresence>
            ))}
        </div>
      </div>
    </div>
  );
};
